import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import { useAuthStore } from "../store/useAuthStore";
import "./LostLeads.css";

const REASON_OPTIONS = [
  "Price",
  "Competitor",
  "No Response",
  "Budget",
  "Cancelled",
];

const REASON_TONE = {
  Price: "amber",
  Competitor: "purple",
  "No Response": "slate",
  Budget: "red",
  Cancelled: "grey",
};

const getReasonTone = (reason) => REASON_TONE[reason] || "slate";

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return "—";
  }

  return `$${amount.toLocaleString("en-US")}`;
};

const getInitials = (value) =>
  String(value || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "?";

const LostLeads = () => {
  const navigate = useNavigate();

  const { authUser } = useAuthStore();

  const currentRole = String(authUser?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = currentRole === "admin";

  /*
    Admin columns:
    Company, Campaign, Contact, Reason, Assigned,
    Lost Date, Value, Action = 8

    Other users:
    Company, Campaign, Contact, Reason, Assigned,
    Lost Date, Action = 7
  */
  const tableColumnCount = isAdmin ? 8 : 7;

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [leads, setLeads] = useState([]);

  const [search, setSearch] = useState("");

  const [reasonFilter, setReasonFilter] = useState("");

  const [ownerFilter, setOwnerFilter] = useState("");

  const [filterCampaign, setFilterCampaign] = useState("all");

  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [data, campaignResponse] = await Promise.all([
        leadsApi.getAll(),
        campaignApi.getAll(),
      ]);

      const allLeads = Array.isArray(data)
        ? data
        : Array.isArray(data?.leads)
          ? data.leads
          : Array.isArray(data?.data)
            ? data.data
            : [];

      const campaignList =
        campaignResponse?.campaigns ||
        campaignResponse?.data?.campaigns ||
        campaignResponse?.data ||
        campaignResponse ||
        [];

      setCampaigns(Array.isArray(campaignList) ? campaignList : []);

      const lostLeads = allLeads.filter(
        (lead) =>
          String(lead?.status || "")
            .trim()
            .toLowerCase() === "lost",
      );

      setLeads(lostLeads);
    } catch (err) {
      console.error("[load-lost-leads-error]", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load lost leads.",
      );
    } finally {
      setLoading(false);
    }
  };

  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);

    const lostLeadCampaigns = leads
      .map((lead) => String(lead?.campaign || "").trim())
      .filter(Boolean);

    return [...new Set([...masterNames, ...lostLeadCampaigns])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [campaigns, leads]);

  const filtered = useMemo(() => {
    const searchText = String(search || "")
      .trim()
      .toLowerCase();

    return leads.filter((lead) => {
      const keyword = `${lead?.company || ""} ${
        lead?.contact || ""
      } ${lead?.email || ""} ${lead?.campaign || ""}`
        .toLowerCase()
        .includes(searchText);

      const reason = !reasonFilter || lead?.lostReason === reasonFilter;

      const assignedValue =
        typeof lead?.assignedTo === "object"
          ? String(lead?.assignedTo?._id || "")
          : String(lead?.assignedTo || "");

      const owner = !ownerFilter || assignedValue === ownerFilter;

      const campaign =
        filterCampaign === "all" ||
        String(lead?.campaign || "").trim() === filterCampaign;

      return keyword && reason && owner && campaign;
    });
  }, [leads, search, reasonFilter, ownerFilter, filterCampaign]);

  const recoverLead = async (lead) => {
    const confirmed = window.confirm("Move this lead back to Negotiation?");

    if (!confirmed) {
      return;
    }

    try {
      await leadsApi.update(lead._id, {
        status: "Negotiation",
        lostReason: "",
      });

      await loadData();
    } catch (err) {
      console.error("[recover-lead-error]", err);

      window.alert(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to recover lead.",
      );
    }
  };

  /*
    Amount calculation is Admin-only.
  */
  const totalValue = useMemo(() => {
    if (!isAdmin) {
      return 0;
    }

    return filtered.reduce((sum, lead) => {
      const amount = Number(lead?.proposalValue || lead?.estValue || 0);

      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [filtered, isAdmin]);

  const topReason = useMemo(() => {
    const counts = {};

    filtered.forEach((lead) => {
      if (!lead?.lostReason) {
        return;
      }

      counts[lead.lostReason] = (counts[lead.lostReason] || 0) + 1;
    });

    const sorted = Object.entries(counts).sort(
      (first, second) => second[1] - first[1],
    );

    return sorted[0]?.[0] || "—";
  }, [filtered]);

  const clearFilters = () => {
    setSearch("");
    setReasonFilter("");
    setOwnerFilter("");
    setFilterCampaign("all");
  };

  const hasFilters =
    Boolean(search.trim()) ||
    Boolean(reasonFilter) ||
    Boolean(ownerFilter) ||
    filterCampaign !== "all";

  return (
    <div className="lost-page">
      {/* Header */}
      <div className="lost-header">
        <div>
          <h1>Lost Leads</h1>

          <p className="lost-subtitle">
            Review, analyze and recover lost opportunities.
          </p>
        </div>

        <button type="button" className="lost-export-btn">
          ⇩ Export CSV
        </button>
      </div>

      {/* KPI cards */}
      <div
        className={["lost-kpi-grid", !isAdmin ? "lost-kpi-grid-user" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <KpiCard tone="red" label="Total Lost" value={filtered.length} />

        {/* Admin-only amount card */}
        {isAdmin && (
          <KpiCard
            tone="blue"
            label="Potential Value"
            value={formatCurrency(totalValue)}
          />
        )}

        <KpiCard tone="amber" label="Top Reason" value={topReason} />
      </div>

      {/* Toolbar */}
      <div className="lost-toolbar">
        <div className="lost-search-box">
          <span className="lost-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search company, contact or email…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <select
          className="lost-filter-select"
          value={filterCampaign}
          onChange={(event) => setFilterCampaign(event.target.value)}
        >
          <option value="all">All Campaigns</option>

          {availableCampaigns.map((campaign) => (
            <option key={campaign} value={campaign}>
              {campaign}
            </option>
          ))}
        </select>

        {hasFilters && (
          <button
            type="button"
            className="lost-clear-filter-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="lost-error">
          <span>{error}</span>

          <button type="button" onClick={loadData}>
            Try Again
          </button>
        </div>
      )}

      {/* Table */}
      <div className="lost-table-card">
        <div className="lost-table-scroll">
          <table className="lost-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Campaign</th>
                <th>Contact</th>
                <th>Reason</th>
                <th>Assigned</th>
                <th>Lost Date</th>

                {/* Admin-only value column */}
                {isAdmin && <th>Value</th>}

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColumnCount} className="lost-state-cell">
                    Loading lost leads…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} className="lost-state-cell">
                    <div className="lost-empty">
                      <div className="lost-empty-icon">🗂️</div>

                      <strong>No lost leads found</strong>

                      <span>
                        {leads.length === 0
                          ? "Leads marked as Lost will show up here."
                          : "No lost leads match your filters."}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((lead) => (
                  <tr key={lead._id}>
                    {/* Company */}
                    <td>
                      <div className="lost-company-cell">
                        <span className="lost-company-avatar">
                          {getInitials(lead?.company)}
                        </span>

                        <strong>{lead?.company || "Unnamed Company"}</strong>
                      </div>
                    </td>

                    {/* Campaign */}
                    <td>{lead?.campaign || "—"}</td>

                    {/* Contact */}
                    <td>{lead?.contact || "—"}</td>

                    {/* Lost reason */}
                    <td>
                      {lead?.lostReason ? (
                        <span
                          className={`lost-reason-badge lost-reason-${getReasonTone(
                            lead.lostReason,
                          )}`}
                        >
                          {lead.lostReason}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Assigned */}
                    <td>
                      {typeof lead?.assignedTo === "object"
                        ? lead?.assignedTo?.name ||
                          lead?.assignedTo?.fullName ||
                          lead?.assignedTo?.email ||
                          "—"
                        : lead?.assignedTo || "—"}
                    </td>

                    {/* Lost date */}
                    <td>{formatDate(lead?.lostAt || lead?.updatedAt)}</td>

                    {/* Admin-only amount */}
                    {isAdmin && (
                      <td className="lost-value-cell">
                        {formatCurrency(lead?.proposalValue || lead?.estValue)}
                      </td>
                    )}

                    {/* Actions */}
                    <td>
                      <div className="lost-actions">
                        <button
                          type="button"
                          className="lost-action-btn lost-action-view"
                          onClick={() => navigate(`/leads/${lead._id}`)}
                        >
                          View
                        </button>

                        <button
                          type="button"
                          className="lost-action-btn lost-action-recover"
                          onClick={() => recoverLead(lead)}
                        >
                          ↻ Recover
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ tone, label, value }) => (
  <div className={`lost-kpi-card lost-kpi-${tone}`}>
    <span className="lost-kpi-label">{label}</span>

    <strong className="lost-kpi-value">{value}</strong>
  </div>
);

export default LostLeads;
